import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Save } from "@material-ui/icons";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import MDEditor from "@uiw/react-md-editor";
import Button from "components/CustomButtons/Button.js";
import NotificationContext from "views/Components/Context.js";
import {
  listArtifacts,
  updateMarkdownArtifact,
  fetchMarkdownFromS3,
} from "views/APIs/NewProjectArtifacts";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    marginBottom: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  actionBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  versionSelect: {
    minWidth: 150,
    marginRight: theme.spacing(2),
  },
  editorContainer: {
    display: "flex",
    height: "500px",
    border: "1px solid #d1d5db",
    borderRadius: "4px",
    overflow: "hidden",
  },
  editorPane: {
    flex: 1,
    overflow: "auto",
    borderRight: "1px solid #d1d5db",
  },
  previewPane: {
    flex: 1,
    overflow: "auto",
    padding: theme.spacing(2),
    backgroundColor: "#fff",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
    fontSize: "16px",
    lineHeight: "1.6",
    "& h1": { fontSize: "2em", fontWeight: 600, marginTop: "24px", marginBottom: "16px" },
    "& h2": { fontSize: "1.5em", fontWeight: 600, marginTop: "24px", marginBottom: "16px" },
    "& h3": { fontSize: "1.25em", fontWeight: 600, marginTop: "24px", marginBottom: "16px" },
    "& p": { marginTop: 0, marginBottom: "16px" },
    "& code": {
      backgroundColor: "rgba(175, 184, 193, 0.2)",
      padding: "0.2em 0.4em",
      borderRadius: "3px",
      fontSize: "90%",
    },
    "& pre": {
      backgroundColor: "#f6f8fa",
      padding: "16px",
      overflow: "auto",
      borderRadius: "3px",
    },
    "& ul, & ol": { paddingLeft: "2em", marginBottom: "16px" },
    "& table": { borderCollapse: "collapse", width: "100%", marginBottom: "16px" },
    "& th, & td": { padding: "6px 13px", border: "1px solid #d0d7de" },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "400px",
    flexDirection: "column",
  },
  noArtifact: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "400px",
    flexDirection: "column",
    color: theme.palette.text.secondary,
  },
  previewHeader: {
    position: "sticky",
    top: 0,
    backgroundColor: "#fff",
    zIndex: 1,
    padding: theme.spacing(1),
    borderBottom: "1px solid #d1d5db",
  },
  wrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: theme.palette.success.main,
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

export default function MarkdownArtifactViewer({
  projectId,
  stage,
  artifactType = "lecture_outline_md", // e.g., "outline_md", "lab_md"
  title = "Outline",
  disabled = false,
  onContentChange,
  onArtifactSelect, // Callback when artifact/version is selected
}) {
  const classes = useStyles();
  const { showNotification } = React.useContext(NotificationContext);

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [artifact, setArtifact] = useState(null);
  const [markdownContent, setMarkdownContent] = useState("");
  const [originalContent, setOriginalContent] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState("");
const didInitDefaultVersion = useRef(false);

  // Fetch artifacts
  const fetchArtifacts = async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const allArtifacts = await listArtifacts(projectId);

      // Find all markdown artifacts for this type
      const candidates = allArtifacts.filter(
        (a) => a.type === artifactType || a.type === `${artifactType}.md`,
      );

      // Group by run_id
      const groupedByRun = candidates.reduce((acc, a) => {
        if (!acc[a.run_id]) {
          acc[a.run_id] = { run_id: a.run_id, artifact: a };
        }
        // If multiple artifacts in same run, keep the first one found or sort
        return acc;
      }, {});

      // Sort runs by ID (newest first)
      const sortedVersions = Object.values(groupedByRun).sort((a, b) =>
        b.run_id.localeCompare(a.run_id),
      );

      setVersions(sortedVersions);

      // Default to the latest run
      const latest = sortedVersions[0] || null;
      setArtifact(latest?.artifact || null);

      if (latest?.artifact) {
        if (!didInitDefaultVersion.current) {
          setSelectedVersion(latest.run_id);
          didInitDefaultVersion.current = true;

          // Notify parent of selected run's artifact
          if (onArtifactSelect) {
            onArtifactSelect(latest.artifact.artifact_id);
          }
        }
        await loadContent(latest.artifact);
      }
    } catch (error) {
      console.error("Error fetching artifacts:", error);
      showNotification("Error loading artifacts", "danger");
    } finally {
      setLoading(false);
    }
  };

  // Load markdown content
  const loadContent = async (artifact) => {
    if (!artifact?.s3?.public_url) return;

    try {
      const data = await fetchMarkdownFromS3(projectId, artifact.s3.public_url);
      const content = data?.markdown ?? "";
      setMarkdownContent(content);
      setOriginalContent(content);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Error loading content:", error);
      showNotification("Error loading markdown content", "danger");
    }
  };

  // Handle content change
  const handleContentChange = (newContent) => {
    setMarkdownContent(newContent);
    setHasUnsavedChanges(newContent !== originalContent);
    
    if (onContentChange) {
      onContentChange(newContent);
    }
  };

  // Save changes
  const handleSave = async () => {
    if (!artifact || !markdownContent) return;

    setSaving(true);
    try {
      await updateMarkdownArtifact(
        projectId,
        artifact.artifact_id,
        markdownContent,
      );
      setOriginalContent(markdownContent);
      setHasUnsavedChanges(false);
      showNotification("Content saved successfully", "success");

      // Refresh data
      await fetchArtifacts();
    } catch (error) {
      console.error("Error saving:", error);
      showNotification("Error saving content", "danger");
    } finally {
      setSaving(false);
    }
  };

  // Handle version selection
  const handleVersionChange = async (event) => {
    const runId = event.target.value;
    setSelectedVersion(runId);

    const version = versions.find((v) => v.run_id === runId);
    if (version?.artifact) {
      setArtifact(version.artifact);
      await loadContent(version.artifact);

      // Notify parent of selected artifact
      if (onArtifactSelect) {
        onArtifactSelect(version.artifact.artifact_id);
      }
    }
  };

  // Download markdown file
  const handleDownload = async () => {
    if (!artifact?.s3?.public_url) return;

    try {
      const data = await fetchMarkdownFromS3(projectId, artifact.s3.public_url);
      const content = data?.markdown ?? "";
      const blob = new Blob([content], { type: "text/markdown" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${title.replace(/\s+/g, "_").toLowerCase()}.md`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showNotification("File downloaded successfully", "success");
    } catch (error) {
      console.error("Error downloading file:", error);
      showNotification("Error downloading file", "danger");
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchArtifacts();
  }, [projectId, artifactType]);

  

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
        <Typography variant="body2" style={{ marginTop: 16 }}>
          Loading {title.toLowerCase()}...
        </Typography>
      </Box>
    );
  }

  if (!artifact) {
    return (
      <Box className={classes.noArtifact}>
        <Typography variant="h5">No {title} Available</Typography>
        <Typography variant="body2">
          Run the pipeline from the previous stage to generate the {title.toLowerCase()}.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h5" style={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>

      {/* Action bar */}
      <Box className={classes.actionBar}>
        {versions.length > 0 && (
          <FormControl
            variant="outlined"
            size="small"
            className={classes.versionSelect}
          >
            <InputLabel>Version</InputLabel>
            <Select
              value={selectedVersion}
              onChange={handleVersionChange}
              label="Version"
            >
              {versions.map((v, idx) => (
                <MenuItem key={v.run_id} value={v.run_id}>
                  Version {versions.length - idx}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {(hasUnsavedChanges || saving) && (
          <div className={classes.wrapper}>
            <Button
              color="success"
              size="sm"
              onClick={handleSave}
              disabled={saving || disabled}
              startIcon={!saving ? <Save /> : null}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
            {saving && (
              <CircularProgress size={24} className={classes.buttonProgress} />
            )}
          </div>
        )}

      </Box>

      <Divider style={{ marginBottom: 16 }} />

      {/* Editor with preview */}
      <Box className={classes.editorContainer}>
        {/* Editor Pane */}
        <div className={classes.editorPane} data-color-mode="light">
          <MDEditor
            value={markdownContent}
            onChange={handleContentChange}
            height={480}
            hideToolbar={false}
            visibleDragbar={false}
            textareaProps={{
              placeholder: "Markdown content will appear here...",
              disabled: disabled,
            }}
          />
        </div>

        
      </Box>

      {/* Download button */}
      <Box style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
        <Button
          color="info"
          size="sm"
          onClick={handleDownload}
          disabled={!artifact}
        >
          Download .md File
        </Button>
      </Box>
    </Box>
  );
}
